import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import { TrustBadges } from '../components/TrustBadges';
import { ServiceCard } from '../components/ServiceCard';
import { FAQ } from '../components/FAQ';
import { Testimonials } from '../components/Testimonials';
import { FileText, TrendingUp, BookOpen, Briefcase } from 'lucide-react';
export function HomePage() {
  return <Layout data-id="element-257">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white py-20 md:py-32 overflow-hidden" data-id="element-258">
        <div className="absolute inset-0 bg-blue-900/20 z-0" data-id="element-259"></div>
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" data-id="element-260"></div>
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl" data-id="element-261"></div>

        <Container className="relative z-10" data-id="element-262">
          <div className="max-w-3xl mx-auto text-center" data-id="element-263">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-white" data-id="element-264">
              Credit & Financial Consulting to help you understand your profile.
            </h1>
            <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-265">
              Education-first guidance and personalized strategies based on your
              financial goals. No gimmicks, just clear, professional advice.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-266">
              <Link to="/contact" data-id="element-267">
                <Button size="lg" className="w-full sm:w-auto" data-id="element-268">
                  Request a Consultation
                </Button>
              </Link>
              <Link to="/services" data-id="element-269">
                <Button variant="outline" size="lg" className="w-full sm:w-auto bg-transparent text-white border-slate-600 hover:bg-slate-800 hover:text-white" data-id="element-270">
                  Explore Services
                </Button>
              </Link>
            </div>
            <p className="mt-6 text-sm text-slate-400" data-id="element-271">
              Secure • Confidential • Education-First
            </p>
          </div>
        </Container>
      </section>

      <TrustBadges data-id="element-272" />

      {/* Services Overview */}
      <section className="py-20 bg-white" data-id="element-273">
        <Container data-id="element-274">
          <div className="text-center mb-16" data-id="element-275">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-276">
              Professional Consulting Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-277">
              We provide the knowledge and strategy you need to make informed
              financial decisions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-278">
            <ServiceCard title="Credit Profile Review" description="A comprehensive analysis of your credit reports to identify errors and areas for improvement." icon={FileText} features={['Report Analysis', 'Error Identification', 'Score Factor Review']} link="/services" data-id="element-279" />
            <ServiceCard title="Credit Education" description="Learn how credit scoring models work and how to build a strong history responsibly." icon={BookOpen} features={['Scoring Models', 'Utilization Strategy', 'History Building']} link="/services" data-id="element-280" />
            <ServiceCard title="Financial Planning" description="Basic financial planning to help you budget, save, and manage debt effectively." icon={TrendingUp} features={['Budgeting Basics', 'Debt Management', 'Savings Goals']} link="/services" data-id="element-281" />
            <ServiceCard title="Readiness Consulting" description="Prepare your profile for major financial milestones like home buying or business funding." icon={Briefcase} features={['Mortgage Readiness', 'Business Credit', 'Application Prep']} link="/services" data-id="element-282" />
          </div>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-id="element-283">
        <Container data-id="element-284">
          <div className="text-center mb-16" data-id="element-285">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-286">
              How We Work With You
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-287">
              Our process is transparent, collaborative, and focused on your
              long-term success.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative" data-id="element-288">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-slate-200 -z-10 transform -translate-y-1/2" data-id="element-289"></div>

            <div className="text-center relative bg-slate-50 p-4" data-id="element-290">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg" data-id="element-291">
                1
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-292">Submit Your Info</h3>
              <p className="text-slate-600" data-id="element-293">
                Fill out our secure intake form to tell us about your current
                situation and financial goals.
              </p>
            </div>
            <div className="text-center relative bg-slate-50 p-4" data-id="element-294">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg" data-id="element-295">
                2
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-296">Consultation</h3>
              <p className="text-slate-600" data-id="element-297">
                Meet with a specialist to review your profile and discuss
                educational strategies tailored to you.
              </p>
            </div>
            <div className="text-center relative bg-slate-50 p-4" data-id="element-298">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg" data-id="element-299">
                3
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-300">Action Plan</h3>
              <p className="text-slate-600" data-id="element-301">
                Receive a personalized roadmap with actionable steps to improve
                your financial health.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-302">
            <Link to="/contact" data-id="element-303">
              <Button size="lg" data-id="element-304">Start Your Journey</Button>
            </Link>
          </div>
        </Container>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white" data-id="element-305">
        <Container data-id="element-306">
          <div className="text-center mb-16" data-id="element-307">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-308">
              Client Experiences
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-309">
              Hear from individuals who have gained clarity and confidence
              through our consulting.
            </p>
          </div>
          <Testimonials data-id="element-310" />
        </Container>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50 border-t border-slate-200" data-id="element-311">
        <Container data-id="element-312">
          <div className="text-center mb-12" data-id="element-313">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-314">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-315">
              Common questions about our services and approach.
            </p>
          </div>
          <FAQ data-id="element-316" />
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white" data-id="element-317">
        <Container data-id="element-318">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-319">
            <div className="max-w-2xl" data-id="element-320">
              <h2 className="text-3xl font-bold mb-4 text-white" data-id="element-321">
                Ready to take control of your financial future?
              </h2>
              <p className="text-blue-100 text-lg" data-id="element-322">
                Schedule a consultation today to get personalized guidance and
                education.
              </p>
            </div>
            <div className="flex-shrink-0" data-id="element-323">
              <Link to="/contact" data-id="element-324">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 shadow-lg border-none" data-id="element-325">
                  Request Consultation
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </Layout>;
}